import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { useTranslation } from 'react-i18next'
import type { WritingAssistantListItem } from '@/lib/types/writing'
import { Card } from '@/components/ui'

interface WritingAssistantFilterBarProps {
  searchQuery: string
  onSearchChange: (value: string) => void
  contentTypeFilter: string
  onContentTypeFilterChange: (value: string) => void
  createdByFilter: string
  onCreatedByFilterChange: (value: string) => void
  writingAssistants: WritingAssistantListItem[]
}

export function WritingAssistantFilterBar({
  searchQuery,
  onSearchChange,
  contentTypeFilter,
  onContentTypeFilterChange,
  createdByFilter,
  onCreatedByFilterChange,
  writingAssistants,
}: WritingAssistantFilterBarProps) {
  const { t } = useTranslation()

  // Get unique users for filter dropdown
  const uniqueUsers = Array.from(
    new Map(
      writingAssistants.filter(wa => wa.createdBy).map(wa => [wa.createdBy!.id, wa.createdBy!])
    ).values()
  )

  // Get unique content types from actual writing assistants
  const uniqueContentTypes = Array.from(new Set(writingAssistants.map(wa => wa.contentType))).sort()

  return (
    <Card className="text-center py-4 px-4 md:px-6 rounded-lg border-x mx-0 md:mx-0 mb-2">
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <Input
            placeholder={t('writing.zoeken')}
            value={searchQuery}
            onChange={e => onSearchChange(e.target.value)}
            className="w-full bg-white h-6"
          />
        </div>
        <div className="sm:w-48">
          <Select value={contentTypeFilter} onValueChange={onContentTypeFilterChange}>
            <SelectTrigger className="w-full bg-white">
              <SelectValue placeholder={t('writing.filterContentType')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('writing.allContentTypes')}</SelectItem>
              {uniqueContentTypes.map(type => (
                <SelectItem key={type} value={type}>
                  {type}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="sm:w-48">
          <Select value={createdByFilter} onValueChange={onCreatedByFilterChange}>
            <SelectTrigger className="w-full bg-white">
              <SelectValue placeholder={t('writing.filterCreatedBy')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('writing.allUsers')}</SelectItem>
              {uniqueUsers.map(user => (
                <SelectItem key={user.id} value={user.id}>
                  {user.displayName || `${user.firstName} ${user.lastName}`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </Card>
  )
}
